import { useEffect, useState } from 'react';
import { getReservationAvailability, type ReservationAvailability as Answer } from '../eventsApi';

export function ReservationAvailability({ inquiryId, dates, time }: { inquiryId: string; dates: string[]; time?: string }) {
  const choices = [...new Set(dates.filter(Boolean))];
  const [selected, setSelected] = useState('');
  const date = choices.includes(selected) ? selected : choices[0] ?? '';
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState<{ key: string; answer: Answer }>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const key = `${inquiryId}/${date}/${time ?? ''}/${refresh}`;
  const answer = result?.key === key ? result.answer : undefined;
  useEffect(() => {
    let active = true;
    setError('');
    if (!date) { setLoading(false); return; }
    setLoading(true);
    getReservationAvailability(inquiryId, date, time).then(({ data }) => {
      if (active) setResult({ key, answer: data });
    }).catch(() => { if (active) setError('Availability could not be checked. Try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [inquiryId, date, time, key]);
  return <section className="rounded-md border border-tribal-border p-3 text-xs space-y-2" aria-label="Reservation availability">
    <div className="flex items-center justify-between gap-2">
      <h4 className="font-semibold text-white">Reservation availability</h4>
      <button type="button" className="text-tribal-red hover:underline disabled:opacity-50" disabled={!date || loading}
        onClick={() => setRefresh(n => n + 1)}>Refresh</button>
    </div>
    {choices.length > 1 && <label className="block text-tribal-muted">Preferred date
      <select className="ml-2 bg-tribal-surface text-white" value={date} onChange={e => setSelected(e.target.value)}>
        {choices.map(value => <option key={value} value={value}>{value}</option>)}
      </select>
    </label>}
    <div role="status" aria-live="polite" className="text-tribal-muted">
      {!date ? 'Choose an event date to check availability.' : loading ? 'Checking availability...' : error || (answer && <>
        {answer.status === 'not_ready' || answer.status === 'error' ? answer.message : <>
          <p>{answer.status === 'closed' ? 'Closed on this date.' : answer.status === 'full' ? 'Full for this group and duration.'
            : answer.status === 'unavailable' ? 'No qualifying start for this date and duration.' : `${answer.bayCount} Bays needed. Qualifying starts:`}</p>
          {!!answer.qualifyingStarts?.length && <p className="text-white mt-1">{answer.qualifyingStarts.map(start => new Date(start)
            .toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })).join(', ')}</p>}
          {answer.checkedAt && <p className="mt-1">Checked {new Date(answer.checkedAt).toLocaleTimeString()}. Availability can change.</p>}
        </>}
      </>)}
    </div>
  </section>;
}
